import type { MeasurementStatus } from '../types/common.js';
import type { CheckResult, FileInfo } from '../types/checks.js';
import { aspectRatio } from '../types/image.js';
import type { ImageMeta } from '../types/image.js';
import type { BackgroundMetrics, FaceGeometryMetrics, QualityMetrics } from '../types/metrics.js';
import type { FaceObservations } from '../types/observations.js';
import type { DocumentProfile } from '../types/profile.js';
import type { ValidationEngineInfo, ValidationReport, VisualMeasurement } from '../types/result.js';
import { MEASUREMENT_STATUSES } from '../types/common.js';
import { clampNormalizedGeometry } from '../types/geometry.js';
import { buildGuides } from './guides.js';

/**
 * Report builder: the single funnel from checks to everything the user sees.
 *
 * Cards, overlay shapes and problem markers all come out of this one function,
 * so the picture on the photo and the list underneath can never disagree.
 */

/** Display order of the cards, mirroring the order used in the product spec. */
export const DISPLAY_ORDER: readonly string[] = [
  // Технические параметры файла — первое, что видит пользователь.
  'aspect_ratio',
  'image_size',
  'file_format',
  'file_size',
  'color_profile',
  'dpi',
  // Лицо и композиция.
  'face_count',
  'face_position',
  'head_size',
  'eye_line',
  'face_pose',
  'head_coverage',
  // Что человек делает и что закрывает лицо.
  'eye_open',
  'mouth',
  'expression',
  'hair_occlusion',
  'headwear',
  'glasses',
  'face_obstruction',
  // Свет.
  'exposure',
  'lighting',
  'lighting_balance',
  'shadow_face',
  'highlight',
  'red_eye',
  // Качество изображения.
  'sharpness',
  'noise',
  'compression_artifacts',
  // Фон.
  'background',
  'background_quality',
  'shadow_background',
  // Digital authenticity.
  'retouching',
  'ai_manipulation',
  'background_replacement',
];

export interface ReportBuilderInput {
  profile: DocumentProfile;
  checks: CheckResult[];
  image: ImageMeta;
  fileInfo: FileInfo;
  faces: FaceObservations;
  geometry?: FaceGeometryMetrics | undefined;
  quality?: QualityMetrics | undefined;
  background?: BackgroundMetrics | undefined;
  engine: ValidationEngineInfo;
  startedAt: number;
  finishedAt: number;
  now: Date;
  warnings: string[];
  location: ValidationEngineInfo['location'];
  reportId: string;
}

const orderOf = (id: string): number => {
  const index = DISPLAY_ORDER.indexOf(id);
  return index === -1 ? DISPLAY_ORDER.length + 1 : index;
};

const statusRank: Record<MeasurementStatus, number> = {
  fail: 0,
  warning: 1,
  uncertain: 2,
  not_checked: 3,
  pass: 4,
};

const severityRank: Record<string, number> = { error: 0, warning: 1, info: 2 };

export const checkToMeasurement = (check: CheckResult): VisualMeasurement => {
  const displayValue = check.details?.['displayValue'];
  return {
    id: check.id,
    label: check.title,
    status: check.status,
    severity: check.severity,
    geometry: (check.geometry ?? []).map(clampNormalizedGeometry),
    ...(check.value !== undefined ? { value: check.value } : {}),
    ...(check.min !== undefined ? { min: check.min } : {}),
    ...(check.max !== undefined ? { max: check.max } : {}),
    ...(check.unit !== undefined ? { unit: check.unit } : {}),
    ...(check.measurement ? { measurement: check.measurement } : {}),
    ...(check.official ? { official: check.official } : {}),
    ...(check.policy ? { policy: check.policy } : {}),
    ...(check.details?.['policyViolation'] === true ? { policyViolation: true } : {}),
    ...(check.durationMs !== undefined ? { durationMs: check.durationMs } : {}),
    message: check.message,
    fixability: check.fixability,
    ...(check.problemRegions && check.problemRegions.length > 0
      ? { markers: check.problemRegions }
      : {}),
    interactive: true,
    ...(typeof displayValue === 'string' ? { displayValue } : {}),
  };
};

export const buildReport = (input: ReportBuilderInput): ValidationReport => {
  const { profile, geometry } = input;

  /**
   * Normalize every shape once, here: cards, overlay and problem markers are all
   * derived from this single array, so the picture on the photo, the list and
   * the markers can never disagree.
   */
  const checks = input.checks.map((check) =>
    check.geometry && check.geometry.length > 0
      ? { ...check, geometry: check.geometry.map(clampNormalizedGeometry) }
      : check,
  );

  const checkById = new Map(checks.map((check) => [check.id, check]));
  const guides = buildGuides({
    profile,
    geometry,
    statusOf: (checkId) => checkById.get(checkId),
  });

  const orderedChecks = [...checks].sort((a, b) => orderOf(a.id) - orderOf(b.id));
  const measurements: VisualMeasurement[] = [...guides, ...orderedChecks.map(checkToMeasurement)];

  const counts = Object.fromEntries(
    MEASUREMENT_STATUSES.map((status) => [status, 0]),
  ) as Record<MeasurementStatus, number>;
  for (const check of checks) counts[check.status] += 1;

  /**
   * Policy findings are treated according to the profile's own declaration:
   *
   *   onViolation: 'warning' → a service note: shown to the user and
   *                            counted as a soft issue, but it can never turn a
   *                            photo into a hard failure;
   *   onViolation: 'error'   → the numeric threshold is algorithmic, but
   *                            crossing it breaks the qualitative official
   *                            requirement, so it becomes a real failure.
   */
  const isPolicyViolation = (check: CheckResult): boolean =>
    check.details?.['policyViolation'] === true;
  /**
   * Only a **genuine** failure may be escalated by the profile's
   * `onViolation: 'error'`. A `warning` stays a warning, and — most important —
   * an `uncertain` result never becomes a hard failure: "we could not decide"
   * must not be reported as "the photo is bad".
   */
  const escalatesToError = (check: CheckResult): boolean =>
    check.status === 'fail' && isPolicyViolation(check) && check.policy?.onViolation === 'error';

  const statusForVerdict = (check: CheckResult): string => {
    if (escalatesToError(check)) return 'fail';
    if (isPolicyViolation(check)) return check.status === 'fail' ? 'warning' : check.status;
    return check.status;
  };
  const severityOf = (check: CheckResult): string =>
    escalatesToError(check) ? 'error' : check.severity;

  const hardFailures = checks.filter(
    (check) => statusForVerdict(check) === 'fail' && severityOf(check) === 'error',
  );
  const softIssues = checks.filter(
    (check) =>
      (statusForVerdict(check) === 'fail' && severityOf(check) !== 'error') ||
      statusForVerdict(check) === 'warning' ||
      check.status === 'uncertain',
  );
  const policyNotes = checks.filter(isPolicyViolation).length;

  const verdict: ValidationReport['overall']['verdict'] =
    hardFailures.length > 0 ? 'fail' : softIssues.length > 0 ? 'needs_review' : 'pass';

  /**
   * User-facing counters. `not_checked` is deliberately its own bucket: a check
   * that never ran must not look like a failure.
   */
  const userSummary = {
    problems: hardFailures.length,
    warnings: checks.filter((check) => statusForVerdict(check) === 'warning').length,
    uncertain: checks.filter((check) => check.status === 'uncertain').length,
    passed: checks.filter((check) => check.status === 'pass').length,
    notChecked: checks.filter((check) => check.status === 'not_checked').length,
  };

  const priority = {
    critical: hardFailures.length,
    warning: checks.filter(
      (check) => severityOf(check) === 'warning' && statusForVerdict(check) !== 'pass',
    ).length,
    info: checks.filter((check) => check.severity === 'info').length,
  };

  const headline =
    verdict === 'fail'
      ? userSummary.problems === 1
        ? 'Фотография не соответствует требованиям: найдена 1 проблема'
        : `Фотография не соответствует требованиям: найдено ${userSummary.problems} проблем(ы)`
      : verdict === 'needs_review'
        ? 'Требуется дополнительная проверка'
        : 'Фотография соответствует проверяемым требованиям';

  const checkedCount = checks.filter((check) => check.status !== 'not_checked').length;
  const parts: string[] = [];
  if (userSummary.problems > 0) parts.push(`❌ ${userSummary.problems} проблем(ы)`);
  if (userSummary.warnings > 0) parts.push(`⚠ ${userSummary.warnings} предупреждени(я)`);
  if (userSummary.uncertain > 0) parts.push(`❓ ${userSummary.uncertain} требует проверки`);
  if (userSummary.passed > 0) parts.push(`✅ ${userSummary.passed} проверок пройдено`);
  if (userSummary.notChecked > 0) parts.push(`— ${userSummary.notChecked} не проверялось`);
  const summary = parts.length > 0 ? parts.join(' · ') : `Проверок: ${checkedCount}.`;

  const topIssues = [...hardFailures, ...softIssues]
    .sort((a, b) => {
      const bySeverity = (severityRank[a.severity] ?? 3) - (severityRank[b.severity] ?? 3);
      if (bySeverity !== 0) return bySeverity;
      const byStatus = statusRank[a.status] - statusRank[b.status];
      if (byStatus !== 0) return byStatus;
      return orderOf(a.id) - orderOf(b.id);
    })
    .slice(0, 6)
    .map((check) => ({
      id: check.id,
      title: check.title,
      message: check.message,
    }));

  const problems = checks.flatMap((check) =>
    (check.problemRegions ?? []).map((region) => ({
      id: region.id,
      checkId: check.id,
      rect: region.rect,
      message: region.message,
      severity: region.severity,
    })),
  );

  return {
    schemaVersion: '1.0',
    reportId: input.reportId,
    createdAt: input.now.toISOString(),
    documentId: profile.id,
    documentName: profile.name,
    engine: {
      ...input.engine,
      capabilityIssues: [
        ...new Set(
          checks
            .filter((check) => check.status === 'not_checked')
            .map((check) => check.message)
            .filter((message) => /недоступ|не загрузил|не рассчитан|не выполняется|не выполнял/i.test(message)),
        ),
      ],
    },
    overall: { verdict, counts, policyNotes, userSummary, priority, headline, summary, topIssues },
    checks,
    measurements,
    problems,
    profile: {
      id: profile.id,
      name: profile.name,
      ...(profile.shortName !== undefined ? { shortName: profile.shortName } : {}),
      ...(profile.country !== undefined ? { country: profile.country } : {}),
      ...(profile.authority !== undefined ? { authority: profile.authority } : {}),
      sourceUrl: profile.sourceUrl,
      sourceCheckedAt: profile.sourceCheckedAt,
      ...(profile.verificationNote !== undefined ? { verificationNote: profile.verificationNote } : {}),
      disclaimer: profile.disclaimer,
      provenance: profile.provenance,
    },
    cta: profile.cta,
    image: {
      width: input.image.width,
      height: input.image.height,
      aspectRatio: aspectRatio(input.image.width, input.image.height),
      ...(input.image.mimeType !== undefined ? { mimeType: input.image.mimeType } : {}),
      ...(input.image.sizeBytes !== undefined ? { sizeBytes: input.image.sizeBytes } : {}),
      ...(input.image.fileName !== undefined ? { fileName: input.image.fileName } : {}),
      ...(input.image.source !== undefined ? { source: input.image.source } : {}),
    },
    metrics: {
      ...(geometry !== undefined ? { geometry } : {}),
      ...(input.quality !== undefined ? { quality: input.quality } : {}),
      ...(input.background !== undefined ? { background: input.background } : {}),
      faces: input.faces,
      fileInfo: input.fileInfo,
    },
    privacy: {
      imageUploadedToServer: input.location === 'server',
      originalStored: false,
    },
    processingMs: Math.max(0, Math.round(input.finishedAt - input.startedAt)),
    warnings: input.warnings,
  };
};

/** Deterministic-enough id without pulling a uuid dependency into the core. */
export const createReportId = (): string => {
  const random = Math.random().toString(36).slice(2, 10);
  return `pv-${Date.now().toString(36)}-${random}`;
};
